import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import {
  AlertCircle,
  Building2,
  CircleDollarSign,
  Factory,
  FileText,
  GitMerge,
  Plus,
  ShieldCheck,
  Tags,
} from 'lucide-react'
import {
  useApVendor,
  useApVendors,
  useCreateVendorCreditMemo,
  useCreateVendorAlias,
  useDeactivateVendorAlias,
  useDecideVendorMerge,
  useExecuteVendorMerge,
  useRequestVendorMerge,
  useUpdateApVendor,
  useVendorAliases,
  useVendorAudit,
  useVendorCredits,
  useVendorInvoices,
  useVendorMergeReviews,
} from '@/api/invoices'
import type {
  ApInvoiceListItem,
  ApVendorCreditDirection,
  ApVendorCreditMemo,
  ApVendorCreditStatus,
  ApVendorSummary,
} from '@/api/invoices'
import { BackButton } from '@/components/back-button'
import { InlineFieldGroup, InlineFieldInput, type InlineGroupField } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/search-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn, formatDateOnly, formatMoney, formatStatus, parseUTCDate } from '@/lib/utils'
import { formatInvoicePriority, formatInvoiceStatus } from '@/lib/status'
import { usePageTitle } from '@/lib/page-title'
import { ComposeInput, ComposeTextarea } from '@/components/compose-field'
import { getApiErrorMessage } from '@/lib/api-error'

interface ApVendorDetailPageProps {
  vendorId: string
}

const UNLINKED_INVOICE_VALUE = '__unlinked__'
type VendorTaxPolicy = 'unknown' | 'tax_exempt' | 'taxable' | 'vendor_specific'
type GovernedKey = 'email' | 'phone' | 'terms' | 'taxPolicy' | 'taxReason' | 'notes'

const TAX_POLICY_OPTIONS: ReadonlyArray<{ value: VendorTaxPolicy; label: string }> = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'tax_exempt', label: 'Tax exempt' },
  { value: 'taxable', label: 'Taxable' },
  { value: 'vendor_specific', label: 'Vendor specific' },
]

const GOVERNED_FIELDS: ReadonlyArray<InlineGroupField<GovernedKey>> = [
  { key: 'email', label: 'Contact email', inputMode: 'email', className: 'col-span-6 sm:col-span-3' },
  { key: 'phone', label: 'Contact phone', inputMode: 'tel', className: 'col-span-6 sm:col-span-3' },
  { key: 'terms', label: 'Default payment terms', className: 'col-span-6 sm:col-span-3' },
  { key: 'taxPolicy', label: 'Tax policy', kind: 'select', options: TAX_POLICY_OPTIONS, className: 'col-span-6 sm:col-span-3' },
  { key: 'taxReason', label: 'Tax policy reason' },
  { key: 'notes', label: 'Vendor notes', kind: 'textarea' },
]

function vendorTaxPolicy(vendor?: ApVendorSummary | null): VendorTaxPolicy {
  return TAX_POLICY_OPTIONS.some((option) => option.value === vendor?.tax_policy)
    ? (vendor?.tax_policy as VendorTaxPolicy)
    : 'unknown'
}

function invoiceVendorName(invoice: ApInvoiceListItem) {
  return invoice.vendor?.name || invoice.vendor_raw || 'AP vendor'
}

function poUuid(invoice: ApInvoiceListItem) {
  return invoice.po_links?.find((link) => link.purchase_order?.uuid || link.purchase_order_uuid)
}

function creditSourceLabel(source: ApVendorCreditMemo['source']) {
  if (source === 'invoice_credit_fields') return 'Invoice credit fields'
  if (source === 'tracker_import') return 'Tracker import'
  return 'Manual'
}

function creditDirectionLabel(direction: ApVendorCreditDirection) {
  return direction === 'credit_issued' ? 'Credit issued' : 'Credit applied'
}

function creditStatusVariant(status: ApVendorCreditStatus) {
  if (status === 'void') return 'destructive' as const
  if (status === 'applied') return 'secondary' as const
  return 'outline' as const
}

function invoiceLabel(invoice: ApInvoiceListItem) {
  const number = invoice.invoice_number || 'Missing invoice #'
  return `${number} · ${formatMoney(invoice.invoice_total)}`
}

function formatMemoDate(date: string) {
  return parseUTCDate(date).toLocaleDateString()
}

function CreditMemoTable({ items }: { items: ApVendorCreditMemo[] }) {
  if (!items.length) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        No credit memos found for this vendor/entity yet.
      </div>
    )
  }

  return (
    <>
      <div className="hidden min-w-0 md:block">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead>Memo</TableHead>
              <TableHead className="w-[136px]">Status</TableHead>
              <TableHead className="w-[164px]">Source</TableHead>
              <TableHead className="w-[132px] text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((memo) => (
              <TableRow key={memo.uuid ?? memo.derived_key ?? memo.id}>
                <TableCell className="min-w-0 align-top">
                  <div className="space-y-1">
                    <p className="font-medium">{creditDirectionLabel(memo.direction)}</p>
                    <p className="line-clamp-2 text-xs text-muted-foreground">
                      {memo.memo_raw || 'No memo note'}
                    </p>
                    {memo.invoice_uuid && (
                      <Link
                        to="/invoices/$invoiceId"
                        params={{ invoiceId: memo.invoice_uuid }}
                        className="inline-flex text-xs text-primary hover:underline"
                      >
                        View linked invoice
                      </Link>
                    )}
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <Badge variant={creditStatusVariant(memo.status)}>{formatStatus(memo.status)}</Badge>
                </TableCell>
                <TableCell className="align-top text-sm">
                  <div>{creditSourceLabel(memo.source)}</div>
                  {memo.derived && <div className="text-xs text-muted-foreground">Derived row</div>}
                </TableCell>
                <TableCell className="text-right align-top tabular-nums">
                  <p className="font-semibold">{formatMoney(memo.amount)}</p>
                  <p className="text-xs text-muted-foreground">{formatMemoDate(memo.created_at)}</p>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2 md:hidden">
        {items.map((memo) => (
          <div key={memo.uuid ?? memo.derived_key ?? memo.id} className="rounded-lg border bg-card p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium">{creditDirectionLabel(memo.direction)}</p>
                <p className="text-xs text-muted-foreground">{creditSourceLabel(memo.source)}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold tabular-nums">{formatMoney(memo.amount)}</p>
                <Badge variant={creditStatusVariant(memo.status)} className="mt-1 text-[10px]">
                  {formatStatus(memo.status)}
                </Badge>
              </div>
            </div>
            <p className="mt-2 line-clamp-3 text-xs text-muted-foreground">
              {memo.memo_raw || 'No memo note'}
            </p>
            {memo.invoice_uuid && (
              <Link
                to="/invoices/$invoiceId"
                params={{ invoiceId: memo.invoice_uuid }}
                className="mt-2 inline-flex text-xs text-primary hover:underline"
              >
                View linked invoice
              </Link>
            )}
          </div>
        ))}
      </div>
    </>
  )
}

function AddCreditMemoDialog({
  vendorId,
  invoices,
}: {
  vendorId: string
  invoices: ApInvoiceListItem[]
}) {
  const [open, setOpen] = useState(false)
  const [invoiceUuid, setInvoiceUuid] = useState(UNLINKED_INVOICE_VALUE)
  const [amount, setAmount] = useState('')
  const [memoRaw, setMemoRaw] = useState('')
  const [externalReference, setExternalReference] = useState('')
  const [originType, setOriginType] = useState<
    'return' | 'exchange' | 'freight_adjustment' | 'overcharge' | 'invoice_credit' | 'unallocated' | 'other'
  >('other')
  const [originReason, setOriginReason] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const createMemo = useCreateVendorCreditMemo(vendorId)

  function resetForm() {
    setInvoiceUuid(UNLINKED_INVOICE_VALUE)
    setAmount('')
    setMemoRaw('')
    setExternalReference('')
    setOriginType('other')
    setOriginReason('')
    setValidationError(null)
    createMemo.reset()
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) resetForm()
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmedAmount = amount.trim()
    const numericAmount = Number(trimmedAmount)
    if (!trimmedAmount) {
      setValidationError('Enter a credit memo amount.')
      return
    }
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setValidationError('Credit memo amount must be greater than zero.')
      return
    }
    if (originType === 'unallocated' && !originReason.trim()) {
      setValidationError('Explain why this credit is not allocated to an invoice, project, or PO.')
      return
    }
    setValidationError(null)
    const idempotencyKey = globalThis.crypto?.randomUUID?.() ?? `credit-${Date.now()}`
    createMemo.mutate(
      {
        amount: trimmedAmount,
        direction: 'credit_issued',
        status: 'open',
        invoice_uuid: invoiceUuid === UNLINKED_INVOICE_VALUE ? null : invoiceUuid,
        memo_raw: memoRaw.trim() || null,
        idempotency_key: idempotencyKey,
        external_reference: externalReference.trim() || null,
        origin_type: originType,
        origin_reason: originReason.trim() || null,
      },
      {
        onSuccess: () => {
          setOpen(false)
          resetForm()
        },
      },
    )
  }

  const submitError = (createMemo.error ? getApiErrorMessage(createMemo.error) : null)

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" /> Add memo
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Add credit memo</DialogTitle>
              <DialogDescription>
                Record an issued vendor credit. Apply it to an invoice separately so the ledger remains traceable.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="credit-amount">Amount</Label>
              <InlineFieldInput
                id="credit-amount"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                aria-invalid={Boolean(validationError)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="credit-origin">Origin</Label>
                <Select value={originType} onValueChange={(value) => setOriginType(value as typeof originType)}>
                  <SelectTrigger id="credit-origin" aria-label="Credit origin" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="return">Return</SelectItem>
                    <SelectItem value="exchange">Exchange</SelectItem>
                    <SelectItem value="freight_adjustment">Freight adjustment</SelectItem>
                    <SelectItem value="overcharge">Overcharge</SelectItem>
                    <SelectItem value="invoice_credit">Invoice credit</SelectItem>
                    <SelectItem value="unallocated">Unallocated</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="credit-external-reference">Vendor memo / reference</Label>
                <InlineFieldInput
                  id="credit-external-reference"
                  value={externalReference}
                  onChange={(event) => setExternalReference(event.target.value)}
                  placeholder="CM-12345"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="credit-origin-reason">Origin / allocation reason</Label>
              <ComposeTextarea
                id="credit-origin-reason"
                value={originReason}
                onChange={(event) => setOriginReason(event.target.value)}
                placeholder="Return, adjustment, or approved reason for leaving the credit unallocated"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="credit-invoice">Linked invoice</Label>
              <SearchSelect
                id="credit-invoice"
                ariaLabel="Linked invoice"
                className="h-9 w-full"
                value={invoiceUuid === UNLINKED_INVOICE_VALUE ? undefined : invoiceUuid}
                allLabel="No linked invoice"
                options={invoices.flatMap((invoice) =>
                  invoice.uuid ? [{ value: invoice.uuid, label: invoiceLabel(invoice) }] : [],
                )}
                onChange={(value) => setInvoiceUuid(value ?? UNLINKED_INVOICE_VALUE)}
                searchPlaceholder="Search invoices"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="credit-memo-note">Memo note</Label>
              <ComposeTextarea
                id="credit-memo-note"
                placeholder="Optional note from vendor statement or accounting workflow"
                value={memoRaw}
                onChange={(event) => setMemoRaw(event.target.value)}
              />
            </div>

            {(validationError || submitError) && (
              <div role="alert" className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{validationError || submitError}</span>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <LoadingButton loading={createMemo.isPending} loadingText="Saving..." type="submit">
                Save memo
              </LoadingButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

function InvoicesPanel({
  dataTotal,
  invoices,
}: {
  dataTotal: number | undefined
  invoices: ApInvoiceListItem[]
}) {
  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" /> Related AP invoices ({dataTotal ?? invoices.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {invoices.length ? (
          <>
            <div className="hidden min-w-0 md:block">
              <Table className="w-full table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Invoice</TableHead>
                    <TableHead className="w-[130px]">Status</TableHead>
                    <TableHead>PO / project</TableHead>
                    <TableHead className="w-[132px] text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => {
                    const firstPo = poUuid(invoice)
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell className="min-w-0 align-top">
                          <Link
                            to="/invoices/$invoiceId"
                            params={{ invoiceId: invoice.uuid! }}
                            className="block truncate font-mono font-medium text-primary hover:underline"
                          >
                            {invoice.invoice_number || 'Missing invoice #'}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {invoiceVendorName(invoice)} · {formatDateOnly(invoice.invoice_date)}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-1 text-xs">
                            <Badge variant="outline">{formatInvoiceStatus(invoice.status)}</Badge>
                            <div className="text-muted-foreground">
                              {formatInvoicePriority(invoice.priority_status)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="min-w-0 align-top">
                          <div className="space-y-1 text-sm">
                            {firstPo?.purchase_order?.uuid || firstPo?.purchase_order_uuid ? (
                              <Link
                                to="/purchase-orders/$poId"
                                params={{
                                  poId:
                                    firstPo.purchase_order?.uuid ?? firstPo.purchase_order_uuid ?? '',
                                }}
                                className="block truncate font-mono text-primary hover:underline"
                              >
                                {firstPo.purchase_order?.po_number || firstPo.po_number_raw}
                              </Link>
                            ) : firstPo?.po_number_raw ? (
                              <span className="font-mono">{firstPo.po_number_raw}</span>
                            ) : (
                              <span className="text-muted-foreground">No linked PO</span>
                            )}
                            {invoice.internal_project && (
                              <Link
                                to="/projects/$projectId"
                                params={{ projectId: invoice.internal_project.uuid! }}
                                className="block truncate text-xs text-primary hover:underline"
                              >
                                {invoice.internal_project.name}
                              </Link>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right align-top tabular-nums">
                          <p className="font-medium">{formatMoney(invoice.invoice_total)}</p>
                          <p className="text-xs text-muted-foreground">
                            Due {formatDateOnly(invoice.official_due_date || invoice.priority_due_date)}
                          </p>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="space-y-2 md:hidden">
              {invoices.map((invoice) => (
                <Link
                  key={invoice.id}
                  to="/invoices/$invoiceId"
                  params={{ invoiceId: invoice.uuid! }}
                  className="block rounded-lg border bg-card p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-medium text-primary">
                        {invoice.invoice_number || 'Missing invoice #'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {invoice.internal_project?.name || invoice.project_ship_to_raw || 'Project pending'}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-semibold">{formatMoney(invoice.invoice_total)}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatDateOnly(invoice.official_due_date || invoice.priority_due_date)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="text-[10px]">
                      {formatInvoiceStatus(invoice.status)}
                    </Badge>
                    <Badge variant="secondary" className="text-[10px]">
                      {formatInvoicePriority(invoice.priority_status)}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No related AP invoices found for this vendor/entity.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function CreditsPanel({
  vendorId,
  invoices,
}: {
  vendorId: string
  invoices: ApInvoiceListItem[]
}) {
  const { data, isLoading, isError, error } = useVendorCredits(vendorId)
  const items = useMemo(() => data?.items ?? [], [data?.items])
  const errorMessage = getApiErrorMessage(error, 'Unable to load vendor credits.')

  return (
    <Card className="min-w-0">
      <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <CircleDollarSign className="h-4 w-4" /> Credit balance
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Vendor credit memos from manual entries, tracker imports, and invoice credit fields.
          </p>
        </div>
        <AddCreditMemoDialog vendorId={vendorId} invoices={invoices} />
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading credit memos...</div>
        ) : isError ? (
          <div role="alert" className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Balance</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {formatMoney(data?.balance)}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Issued</p>
                <p className="mt-1 text-lg font-semibold tabular-nums">
                  {formatMoney(data?.credit_issued_total)}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Applied</p>
                <p className="mt-1 text-lg font-semibold tabular-nums">
                  {formatMoney(data?.credit_applied_total)}
                </p>
              </div>
            </div>
            <CreditMemoTable items={items} />
          </>
        )}
      </CardContent>
    </Card>
  )
}

function mutationError(...values: Array<{ error: unknown }>) {
  const found = values.map((value) => value.error).find((error) => error instanceof Error)
  return found instanceof Error ? found.message : null
}

/** One label/value line of the governed details, styled like the entity details. */
function GovernedDetail({
  label,
  value,
  wide = false,
  valueClassName,
}: {
  label: string
  value?: string | null
  wide?: boolean
  valueClassName?: string
}) {
  return (
    <span className={cn('block', wide && 'sm:col-span-2')}>
      <span className="block text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={cn('block', valueClassName, !value && 'text-muted-foreground')}>
        {value || 'Not set'}
      </span>
    </span>
  )
}

function VendorGovernancePanel({
  vendorId,
  vendor,
}: {
  vendorId: string
  vendor?: ApVendorSummary | null
}) {
  const numericVendorId = Number(vendorId)
  const aliases = useVendorAliases(vendorId)
  const audit = useVendorAudit(vendorId)
  const reviews = useVendorMergeReviews(vendorId)
  const vendors = useApVendors({ limit: 500 })
  const addAlias = useCreateVendorAlias(vendorId)
  const deactivateAlias = useDeactivateVendorAlias(vendorId)
  const updateVendor = useUpdateApVendor(vendorId)
  const requestMerge = useRequestVendorMerge(vendorId)
  const decideMerge = useDecideVendorMerge(vendorId)
  const executeMerge = useExecuteVendorMerge(vendorId)
  const [alias, setAlias] = useState('')
  const [aliasReason, setAliasReason] = useState('')
  const [deactivationReason, setDeactivationReason] = useState('')
  const [targetVendorId, setTargetVendorId] = useState('')
  const [mergeReason, setMergeReason] = useState('')
  const [decisionReason, setDecisionReason] = useState('')
  const [executeConfirmation, setExecuteConfirmation] = useState('')
  const [editingDetails, setEditingDetails] = useState(false)

  const targetVendors = (vendors.data ?? []).filter(
    (candidate) => candidate.id !== numericVendorId && candidate.is_active !== false,
  )
  const error = mutationError(
    addAlias,
    deactivateAlias,
    updateVendor,
    requestMerge,
    decideMerge,
    executeMerge,
  )

  function submitAlias(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!alias.trim() || !aliasReason.trim()) return
    addAlias.mutate(
      { alias: alias.trim(), reason: aliasReason.trim() },
      {
        onSuccess: () => {
          setAlias('')
          setAliasReason('')
        },
      },
    )
  }

  function submitVendorUpdate(next: Record<GovernedKey, string>, changeReason: string) {
    if (!changeReason.trim()) return
    updateVendor.mutate(
      {
        contact_email: next.email.trim() || null,
        contact_phone: next.phone.trim() || null,
        payment_terms_raw: next.terms.trim() || null,
        tax_policy: next.taxPolicy as VendorTaxPolicy,
        tax_policy_reason: next.taxReason.trim() || null,
        notes: next.notes.trim() || null,
        change_reason: changeReason.trim(),
      },
      { onSuccess: () => setEditingDetails(false) },
    )
  }

  function submitMerge(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const target = Number(targetVendorId)
    if (!Number.isFinite(numericVendorId) || !Number.isFinite(target) || !mergeReason.trim()) return
    requestMerge.mutate(
      {
        source_vendor_id: numericVendorId,
        target_vendor_id: target,
        reason: mergeReason.trim(),
      },
      {
        onSuccess: () => {
          setTargetVendorId('')
          setMergeReason('')
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      {vendor?.is_active === false && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          This vendor is inactive
          {vendor.merged_into_vendor_id ? ` and was merged into vendor ${vendor.merged_into_vendor_id}` : ''}.
          Historical invoices remain retained.
        </div>
      )}

      {error && (
        <div role="alert" className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" /> Governed vendor details
          </CardTitle>
          <p className="text-sm text-muted-foreground">Every saved change requires a reason and is written to vendor audit history.</p>
        </CardHeader>
        <CardContent>
          {/* Click to edit in place; Save and the change reason show once something changed (USA-1536). */}
          <InlineFieldGroup
            label="governed details"
            fields={GOVERNED_FIELDS}
            values={{
              email: vendor?.contact_email ?? '',
              phone: vendor?.contact_phone ?? '',
              terms: vendor?.payment_terms_raw ?? '',
              taxPolicy: vendorTaxPolicy(vendor),
              taxReason: vendor?.tax_policy_reason ?? '',
              notes: vendor?.notes ?? '',
            }}
            // The old email box refused a malformed address before saving; keep that guard.
            invalid={(draft) => (draft.email.trim() && !/^[^\s@]+@[^\s@]+$/.test(draft.email.trim()) ? ['email'] : [])}
            reason={{ label: 'Change reason' }}
            saveLabel="Save governed details"
            pending={updateVendor.isPending}
            disabled={vendor?.is_active === false}
            editing={editingDetails}
            onEditingChange={setEditingDetails}
            onSave={submitVendorUpdate}
          >
            <span className="grid gap-3 text-sm sm:grid-cols-2">
              <GovernedDetail label="Contact email" value={vendor?.contact_email} valueClassName="break-all" />
              <GovernedDetail label="Contact phone" value={vendor?.contact_phone} />
              <GovernedDetail label="Default payment terms" value={vendor?.payment_terms_raw} />
              <GovernedDetail label="Tax policy" value={TAX_POLICY_OPTIONS.find((option) => option.value === vendorTaxPolicy(vendor))?.label} />
              <GovernedDetail label="Tax policy reason" value={vendor?.tax_policy_reason} wide />
              <GovernedDetail label="Vendor notes" value={vendor?.notes} wide valueClassName="whitespace-pre-wrap break-words" />
            </span>
          </InlineFieldGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Tags className="h-4 w-4" /> Vendor aliases</CardTitle>
          <p className="text-sm text-muted-foreground">Aliases improve matching but never silently merge vendors.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={submitAlias} className="grid gap-3 sm:grid-cols-[1fr_1.5fr_auto]">
            <InlineFieldInput aria-label="New vendor alias" value={alias} onChange={(event) => setAlias(event.target.value)} placeholder="Invoice or sender alias" />
            <ComposeInput aria-label="Alias reason" value={aliasReason} onChange={(event) => setAliasReason(event.target.value)} placeholder="Why this alias belongs to the vendor" />
            <LoadingButton type="submit" loading={addAlias.isPending} disabled={vendor?.is_active === false || !alias.trim() || !aliasReason.trim()}>Add alias</LoadingButton>
          </form>
          <ComposeInput aria-label="Alias deactivation reason" value={deactivationReason} onChange={(event) => setDeactivationReason(event.target.value)} placeholder="Reason required before deactivating an alias" />
          <div className="space-y-2">
            {(aliases.data ?? []).map((item) => (
              <div key={item.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div><p className="font-medium">{item.alias}</p><p className="text-xs text-muted-foreground">{item.source || 'manual'} · {item.reason || 'No reason retained'}</p></div>
                <div className="flex items-center gap-2"><Badge variant={item.is_active ? 'default' : 'outline'}>{item.is_active ? 'Active' : 'Inactive'}</Badge>{item.is_active && <LoadingButton size="sm" variant="outline" loading={deactivateAlias.isPending} disabled={!deactivationReason.trim()} onClick={() => deactivateAlias.mutate({ aliasId: item.id, reason: deactivationReason.trim() }, { onSuccess: () => setDeactivationReason('') })}>Deactivate</LoadingButton>}</div>
              </div>
            ))}
            {!aliases.isLoading && !(aliases.data ?? []).length && <p className="text-sm text-muted-foreground">No governed aliases yet.</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><GitMerge className="h-4 w-4" /> Reviewable vendor merge</CardTitle>
          <p className="text-sm text-muted-foreground">A request must be finance-approved before execution. The source vendor is retained as inactive; invoices, credits, aliases, and audit history remain traceable.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={submitMerge} className="grid gap-3 sm:grid-cols-[1fr_1.5fr_auto]">
            <SearchSelect ariaLabel="Merge target vendor" className="h-9 w-full" value={targetVendorId || undefined} options={targetVendors.map((candidate) => ({ value: String(candidate.id), label: candidate.name }))} onChange={(value) => value && setTargetVendorId(value)} placeholder="Select canonical target" searchPlaceholder="Search vendors" />
            <ComposeInput aria-label="Merge request reason" value={mergeReason} onChange={(event) => setMergeReason(event.target.value)} placeholder="Evidence that these represent one vendor" />
            <LoadingButton type="submit" loading={requestMerge.isPending} disabled={vendor?.is_active === false || !targetVendorId || !mergeReason.trim()}>Request review</LoadingButton>
          </form>
          <ComposeInput aria-label="Merge decision reason" value={decisionReason} onChange={(event) => setDecisionReason(event.target.value)} placeholder="Finance decision / execution reason" />
          <ComposeInput aria-label="Merge execution confirmation" value={executeConfirmation} onChange={(event) => setExecuteConfirmation(event.target.value)} placeholder="For approved review, type sourceId->targetId before execution" />
          <div className="space-y-2">
            {(reviews.data ?? []).map((review) => {
              const phrase = `${review.source_vendor_id}->${review.target_vendor_id}`
              return <div key={review.uuid} className="rounded-md border p-3"><div className="flex flex-wrap items-start justify-between gap-2"><div><p className="font-medium">Vendor {review.source_vendor_id} → vendor {review.target_vendor_id}</p><p className="text-xs text-muted-foreground">{review.request_reason}</p></div><Badge variant={review.status === 'completed' ? 'default' : 'outline'}>{formatStatus(review.status)}</Badge></div><div className="mt-3 flex flex-wrap gap-2">{review.status === 'pending' && <><LoadingButton size="sm" loading={decideMerge.isPending} disabled={!decisionReason.trim()} onClick={() => decideMerge.mutate({ reviewUuid: review.uuid, decision: 'approved', reason: decisionReason.trim() })}>Approve</LoadingButton><LoadingButton size="sm" variant="outline" loading={decideMerge.isPending} disabled={!decisionReason.trim()} onClick={() => decideMerge.mutate({ reviewUuid: review.uuid, decision: 'rejected', reason: decisionReason.trim() })}>Reject</LoadingButton></>}{review.status === 'approved' && <LoadingButton size="sm" loading={executeMerge.isPending} disabled={!decisionReason.trim() || executeConfirmation !== phrase} onClick={() => executeMerge.mutate({ reviewUuid: review.uuid, sourceVendorId: review.source_vendor_id, targetVendorId: review.target_vendor_id, reason: decisionReason.trim() })}>Execute retained-history merge</LoadingButton>}</div></div>
            })}
            {!reviews.isLoading && !(reviews.data ?? []).length && <p className="text-sm text-muted-foreground">No merge reviews involve this vendor.</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Vendor audit history</CardTitle></CardHeader>
        <CardContent className="space-y-2">{(audit.data ?? []).map((event) => <div key={event.uuid} className="rounded-md border p-3"><div className="flex flex-wrap items-center justify-between gap-2"><p className="font-medium">{formatStatus(event.event_type)}</p><span className="text-xs text-muted-foreground">{formatMemoDate(event.created_at)}</span></div><p className="mt-1 text-xs text-muted-foreground">{event.description || 'No description'} · {event.actor_name || 'system'}</p></div>)}{!audit.isLoading && !(audit.data ?? []).length && <p className="text-sm text-muted-foreground">No vendor governance events recorded yet.</p>}</CardContent>
      </Card>
    </div>
  )
}

export function ApVendorDetailPage({ vendorId }: ApVendorDetailPageProps) {
  const invoiceQuery = useVendorInvoices(vendorId, {
    limit: 50,
    sort_by: 'updated_at',
    sort_dir: 'desc',
  })
  const vendorQuery = useApVendor(vendorId)
  const invoices = invoiceQuery.data?.items ?? []
  const vendor = vendorQuery.data ?? invoices.find((invoice) => invoice.vendor)?.vendor
  const displayName = vendor?.name || invoices[0]?.vendor_raw || `Vendor ${vendorId}`
  usePageTitle(vendor?.name || invoices[0]?.vendor_raw)
  const loadError = [vendorQuery.error, invoiceQuery.error].find(
    (value): value is Error => value instanceof Error,
  )
  const errorMessage = loadError?.message ?? 'Unable to load AP vendor.'

  if (vendorQuery.isLoading || invoiceQuery.isLoading) {
    return <div className="py-12 text-center text-muted-foreground">Loading AP vendor...</div>
  }

  if (vendorQuery.isError || invoiceQuery.isError) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-6 pb-24">
        <BackButton fallback={{ to: '/invoices' }} fallbackLabel="AP Invoices" />
        <div role="alert" className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 pb-24">
      <BackButton fallback={{ to: '/invoices' }} fallbackLabel="AP Invoices" />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold tracking-tight">{displayName}</h1>
          {vendor?.entity_type && <Badge variant="secondary">{formatStatus(vendor.entity_type)}</Badge>}
          {vendor?.is_active === false && <Badge variant="destructive">Inactive</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">
          AP vendor/entity surface. Related invoices are shown with linked manufacturer, purchase
          order, project navigation, and vendor credit balance.
        </p>
      </div>

      <div className="grid min-w-0 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <Card className="min-w-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4" /> Entity details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Name</p>
                <p className="font-medium">{displayName}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Entity type</p>
                <p>{vendor?.entity_type ? formatStatus(vendor.entity_type) : 'Unknown'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Tax policy</p>
                <p>{vendor?.tax_policy ? formatStatus(vendor.tax_policy) : 'Unknown'}</p>
              </div>
              {vendor?.contact_email && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Email</p>
                  <p className="break-all">{vendor.contact_email}</p>
                </div>
              )}
              {vendor?.contact_phone && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Phone</p>
                  <p>{vendor.contact_phone}</p>
                </div>
              )}
              {vendor?.payment_terms_raw && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Payment terms
                  </p>
                  <p>{vendor.payment_terms_raw}</p>
                </div>
              )}
              {vendor?.notes && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Notes</p>
                  <p className="whitespace-pre-wrap break-words">{vendor.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="min-w-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Factory className="h-4 w-4" /> Linked manufacturer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {vendor?.manufacturer?.uuid ? (
                <>
                  <p className="font-medium">{vendor.manufacturer.name}</p>
                  <Link
                    to="/manufacturers/$manuId"
                    params={{ manuId: vendor.manufacturer.uuid }}
                    className="inline-flex text-primary hover:underline"
                  >
                    View manufacturer
                  </Link>
                </>
              ) : (
                <p className="text-muted-foreground">No linked manufacturer</p>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="invoices" className="min-w-0">
          <TabsList className="w-full justify-start overflow-x-auto" aria-label="Vendor detail tabs">
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="credits">Credits</TabsTrigger>
            <TabsTrigger value="governance">Governance</TabsTrigger>
          </TabsList>
          <TabsContent value="invoices" className="min-w-0">
            <InvoicesPanel dataTotal={invoiceQuery.data?.total} invoices={invoices} />
          </TabsContent>
          <TabsContent value="credits" className="min-w-0">
            <CreditsPanel vendorId={vendorId} invoices={invoices} />
          </TabsContent>
          <TabsContent value="governance" className="min-w-0">
            <VendorGovernancePanel vendorId={vendorId} vendor={vendor} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
